/*
 * What a board reaches, read off its tiles.
 *
 * A share's scope is DERIVED from the frozen composition, never authored beside
 * it: every {controllerId, symbolId} pair a tile's config names is a read, a
 * model tile's model is shown, and a commands tile carries Start, Stop and
 * whatever the model declares. The same derivation runs on the sharer's side
 * (to tell the gateway the ceiling) and on the viewer's side (to build the
 * grant the tiles render under), so the two cannot disagree.
 *
 * Symbol ids here are in CONFIG form — the decoded path a tile binds — which is
 * the form the hosts check reads in. The wire wants subject encoding; the
 * bridge encodes on the way out and decodes on the way back.
 */
import type { DashboardTile } from '../../core/domain/dashboard';
import type { CapabilityGrant, ReadGrant, CommandGrant } from './capability';

export interface ShareRead {
  controllerId: string;
  symbolId: string;
}
export interface ShareCommandRef {
  modelId: string;
  command: string;
}
export interface ShareScope {
  reads: ShareRead[];
  modelIds: string[];
  commands: ShareCommandRef[];
}

/** The two commands every model has, before whatever it declares. */
const LIFECYCLE = ['start', 'stop'];

/**
 * Derive the scope from tiles. `declaredCommands` answers what a model declares
 * beyond the lifecycle pair (the sharer's model manager knows; a viewer never
 * needs to ask, the stored scope already says).
 */
export function scopeFromTiles(tiles: readonly DashboardTile[], declaredCommands: (modelId: string) => readonly string[] = () => []): ShareScope {
  const reads = new Map<string, ShareRead>();
  const models = new Set<string>();
  const commands = new Map<string, ShareCommandRef>();
  const noteRead = (controllerId: unknown, symbolId: unknown): void => {
    if (typeof controllerId !== 'string' || typeof symbolId !== 'string' || !controllerId || !symbolId) return;
    reads.set(`${controllerId}\0${symbolId}`, { controllerId, symbolId });
  };
  // Any object anywhere in a config that names both is a binding: the catalog's
  // widgets bind at the top level, the ideas bind lists of them.
  const walk = (v: unknown, depth: number): void => {
    if (depth > 6 || v === null || typeof v !== 'object') return;
    if (Array.isArray(v)) {
      for (const x of v) walk(x, depth + 1);
      return;
    }
    const o = v as Record<string, unknown>;
    noteRead(o['controllerId'], o['symbolId']);
    for (const x of Object.values(o)) walk(x, depth + 1);
  };
  for (const tile of tiles) {
    walk(tile.config, 0);
    if (tile.source.startsWith('native:model-')) {
      const modelId = tile.config['modelId'];
      if (typeof modelId !== 'string' || !modelId) continue;
      models.add(modelId);
      if (tile.source === 'native:model-commands') {
        for (const c of [...LIFECYCLE, ...declaredCommands(modelId)]) commands.set(`${modelId}\0${c}`, { modelId, command: c });
      }
    }
  }
  return { reads: [...reads.values()], modelIds: [...models], commands: [...commands.values()] };
}

/** The grant a viewer's tiles render under, from the scope the gateway stored. */
export function grantFromShare(gatewayId: string, scope: ShareScope, expiresAtMs: number | null, allowInteraction: boolean): CapabilityGrant {
  const reads = new Map<string, ReadGrant>();
  for (const r of scope.reads) {
    const g = reads.get(r.controllerId) ?? { gatewayId, controllerId: r.controllerId, symbolIds: [] };
    if (!g.symbolIds.includes(r.symbolId)) g.symbolIds.push(r.symbolId);
    reads.set(r.controllerId, g);
  }
  const commands = new Map<string, CommandGrant>();
  if (allowInteraction) {
    for (const c of scope.commands) {
      const g = commands.get(c.modelId) ?? { gatewayId, modelId: c.modelId, commands: [] };
      if (!g.commands.includes(c.command)) g.commands.push(c.command);
      commands.set(c.modelId, g);
    }
  }
  return { kind: 'share', reads: [...reads.values()], writes: [], commands: [...commands.values()], expiresAtMs };
}

/** Expiry presets the Share sheet offers, in ms; the cap is the gateway's too. */
export const SHARE_MAX_TTL_MS = 30 * 24 * 3_600_000;
export const SHARE_TTL_PRESETS: { id: string; ms: number }[] = [
  { id: 'hour', ms: 3_600_000 },
  { id: 'day', ms: 24 * 3_600_000 },
  { id: 'week', ms: 7 * 24 * 3_600_000 },
  { id: 'month', ms: SHARE_MAX_TTL_MS },
];
